import styled, { css, type DefaultTheme } from 'styled-components';

// Handle mixes use `primaryBold`, not `primary`. Globally primary-tinted
// (not per-variant) so ports read as a consistent affordance everywhere.
const primaryBoldFor = (theme: DefaultTheme): string =>
  theme.colors?.primaryBold ?? '#8E8DFF';

const outlineFor = (theme: DefaultTheme): string =>
  theme.colors?.outline ?? '#3B3D52';

const outlineVariantFor = (theme: DefaultTheme): string =>
  theme.colors?.outlineVariant ?? '#25263A';

const surfaceContainerHighFor = (theme: DefaultTheme): string =>
  theme.colors?.surfaceContainerHigh ?? '#1F2030';

const primaryEmphasisFor = (theme: DefaultTheme): string =>
  theme.colors?.primaryEmphasis ?? '#CFC9F5';

const isLightTheme = (theme: DefaultTheme): boolean => !!theme.isLightTheme;

const pillBgFor = (theme: DefaultTheme): string =>
  isLightTheme(theme)
    ? surfaceContainerHighFor(theme)
    : `color-mix(in oklch, ${primaryBoldFor(theme)} 42%, ${outlineVariantFor(theme)})`;

const pillBorderFor = (theme: DefaultTheme): string =>
  isLightTheme(theme)
    ? outlineVariantFor(theme)
    : `color-mix(in oklch, ${primaryBoldFor(theme)} 50%, ${outlineVariantFor(theme)})`;

const lavenderBorderFor = (theme: DefaultTheme): string =>
  isLightTheme(theme)
    ? `color-mix(in oklch, ${primaryBoldFor(theme)} 70%, white)`
    : `color-mix(in oklch, ${primaryEmphasisFor(theme)} 70%, ${outlineVariantFor(theme)})`;

// The React Flow `<Handle>` sits inside, fully transparent — only for
// hit-testing + wire anchoring.
export const EdgeDotIn = styled.div`
  position: absolute;
  left: -7px;
  top: 26px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ theme }) => pillBgFor(theme)};
  border: 1.5px solid
    ${({ theme }) => (isLightTheme(theme) ? outlineFor(theme) : pillBorderFor(theme))};
  box-shadow: ${({ theme }) =>
    isLightTheme(theme)
      ? '0 1px 2px 0 rgba(20, 20, 50, 0.06)'
      : `inset 0 1px 0 0 color-mix(in oklch, white 12%, transparent),
         0 2px 4px rgb(0 0 0 / 0.32)`};
  pointer-events: none;
  z-index: 3;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    box-shadow 140ms ease;

  & .react-flow__handle {
    position: absolute;
    inset: 0;
    width: auto !important;
    height: auto !important;
    border-radius: 50%;
    background: transparent !important;
    border: 0 !important;
    transform: none !important;
    pointer-events: auto;
  }
`;

export const EdgePillOut = styled.div`
  position: absolute;
  right: -11px;
  top: calc(50% - 19px);
  width: 22px;
  height: 38px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) =>
    isLightTheme(theme)
      ? (theme.colors?.onPrimaryContainer ?? '#0A006B')
      : 'color-mix(in oklch, white 70%, transparent)'};
  background: ${({ theme }) =>
    isLightTheme(theme)
      ? `color-mix(in oklch, ${primaryBoldFor(theme)} 28%, white)`
      : pillBgFor(theme)};
  border: 1px solid
    ${({ theme }) =>
      isLightTheme(theme)
        ? `color-mix(in oklch, ${primaryBoldFor(theme)} 70%, white)`
        : pillBorderFor(theme)};
  box-shadow: ${({ theme }) =>
    isLightTheme(theme)
      ? '0 1px 2px 0 rgba(20, 20, 50, 0.06)'
      : '0 2px 6px rgb(0 0 0 / 0.25)'};
  cursor: crosshair;
  padding: 0;
  z-index: 3;
  transition:
    transform 120ms ease,
    background 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease,
    color 120ms ease;

  &::before {
    content: '';
    position: absolute;
    inset: -10px -14px;
    border-radius: 999px;
    background: transparent;
  }

  &:hover {
    transform: scale(1.08);
    ${({ theme }) =>
      isLightTheme(theme)
        ? `
          background: ${primaryBoldFor(theme)};
          border-color: white;
          color: ${theme.colors?.onPrimary ?? '#FFFFFF'};
          box-shadow:
            0 0 0 2px color-mix(in oklch, ${primaryBoldFor(theme)} 28%, transparent),
            0 0 8px color-mix(in oklch, ${primaryBoldFor(theme)} 35%, transparent);
        `
        : `
          background: ${primaryEmphasisFor(theme)};
          border-color: ${lavenderBorderFor(theme)};
          color: ${theme.colors?.onPrimaryEmphasis ?? '#1E1A8A'};
        `}
  }

  & svg {
    display: block;
    margin-left: 1px;
    pointer-events: none;
  }

  & .react-flow__handle {
    position: absolute;
    inset: 0;
    width: auto !important;
    height: auto !important;
    border-radius: 999px;
    background: transparent !important;
    border: 0 !important;
    transform: none !important;
    pointer-events: auto;
  }
`;

export const EdgePillCount = styled.span`
  position: absolute;
  top: -12px;
  right: -14px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: ${({ theme }) => primaryBoldFor(theme)};
  color: ${({ theme: { colors = {} } }) => colors.surface ?? '#0E0F18'};
  font-family: ui-monospace, 'Source Code Pro', Menlo, Monaco, Consolas, monospace;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px
    ${({ theme: { colors = {} } }) => colors.surface ?? '#0E0F18'};
  pointer-events: none;
  z-index: 4;
`;

// Descendant selectors (no `>`) so overrides apply whether PortLayer
// is inside `<Body>` (expanded) or directly in `<Root>` (collapsed).
export const PortLayer = styled.div`
  [data-state='selected'] &,
  [data-hovered='true'] & {
    ${EdgeDotIn} {
      ${({ theme }) =>
        isLightTheme(theme)
          ? `
            background: ${primaryBoldFor(theme)};
            border-color: white;
            box-shadow:
              0 0 0 3px color-mix(in oklch, ${primaryBoldFor(theme)} 28%, transparent),
              0 0 8px color-mix(in oklch, ${primaryBoldFor(theme)} 35%, transparent);
          `
          : `
            background: ${primaryEmphasisFor(theme)};
            border-color: ${lavenderBorderFor(theme)};
            box-shadow:
              inset 0 1px 0 0 color-mix(in oklch, white 25%, transparent),
              0 2px 4px rgb(0 0 0 / 0.32),
              0 0 8px color-mix(in oklch, ${primaryEmphasisFor(theme)} 45%, transparent);
          `}
    }
    ${EdgePillOut} {
      ${({ theme }) =>
        isLightTheme(theme)
          ? `
            background: ${primaryBoldFor(theme)};
            border-color: white;
            color: ${theme.colors?.onPrimary ?? '#FFFFFF'};
            box-shadow:
              0 0 0 2px color-mix(in oklch, ${primaryBoldFor(theme)} 28%, transparent),
              0 0 8px color-mix(in oklch, ${primaryBoldFor(theme)} 35%, transparent);
          `
          : `
            background: ${primaryEmphasisFor(theme)};
            border-color: ${lavenderBorderFor(theme)};
            color: ${theme.colors?.onPrimaryEmphasis ?? '#1E1A8A'};
          `}
    }
  }

  /* Output variant: pill carries the "final deliverable" yellow at rest
     AND on hover/selected. Dark-only — the light cascade (Tier 1.5)
     flattens all pills to cream so the design accepts the loss of
     yellow identity in light. The mid-tone border literal (#E8C268)
     is the design's gate-strong; there's no theme token for it because
     it isn't reused elsewhere. */
  ${({ theme }) =>
    !isLightTheme(theme) &&
    css`
      [data-variant='output'] & ${EdgePillOut} {
        background: color-mix(
          in oklch,
          var(--node-card-accent-strong) 38%,
          ${outlineVariantFor(theme)}
        );
        border-color: color-mix(in oklch, #e8c268 55%, ${outlineVariantFor(theme)});
        color: color-mix(in oklch, var(--node-card-accent-ink) 60%, white);
        transform: none;
      }
      [data-variant='output']:hover & ${EdgePillOut},
      [data-variant='output'][data-state='selected'] & ${EdgePillOut},
      [data-variant='output'][data-hovered='true'] & ${EdgePillOut},
      [data-variant='output'] & ${EdgePillOut}:hover {
        background: var(--node-card-accent-strong);
        border-color: color-mix(in oklch, #e8c268 75%, ${outlineVariantFor(theme)});
        color: var(--node-card-accent-ink);
        transform: none;
      }
    `}

  /* runDone selectors include every :hover / [data-hovered] chain that
     the output-variant rule above can match, so green wins on ties. */
  [data-state='runDone'] & ${EdgeDotIn},
  [data-state='runDone'] & ${EdgePillOut},
  [data-state='runDone']:hover & ${EdgeDotIn},
  [data-state='runDone']:hover & ${EdgePillOut},
  [data-state='runDone'][data-hovered='true'] & ${EdgeDotIn},
  [data-state='runDone'][data-hovered='true'] & ${EdgePillOut},
  [data-state='runDone'] & ${EdgeDotIn}:hover,
  [data-state='runDone'] & ${EdgePillOut}:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.successFixed ?? '#4ADE80'};
    border-color: color-mix(
      in oklch,
      ${({ theme: { colors = {} } }) => colors.successFixed ?? '#4ADE80'} 80%,
      white 15%
    );
    box-shadow: ${({ theme }) =>
      isLightTheme(theme)
        ? `0 1px 2px 0 rgba(20, 20, 50, 0.06),
           0 0 8px color-mix(in oklch, ${theme.colors?.successFixed ?? '#4ADE80'} 35%, transparent)`
        : `inset 0 1px 0 0 color-mix(in oklch, white 25%, transparent),
           0 2px 4px rgb(0 0 0 / 0.32),
           0 0 8px color-mix(in oklch, ${theme.colors?.successFixed ?? '#4ADE80'} 45%, transparent)`};
  }
  [data-state='runDone'] & ${EdgePillOut},
  [data-state='runDone']:hover & ${EdgePillOut},
  [data-state='runDone'][data-hovered='true'] & ${EdgePillOut},
  [data-state='runDone'] & ${EdgePillOut}:hover {
    color: ${({ theme: { colors = {} } }) => colors.onSuccess ?? '#002106'};
  }

  [data-drop-target='true'] & ${EdgeDotIn} {
    background: ${({ theme }) => primaryBoldFor(theme)};
    border-color: white;
    box-shadow:
      0 0 0 4px
        color-mix(in oklch, ${({ theme }) => primaryBoldFor(theme)} 26%, transparent),
      0 0 14px
        color-mix(in oklch, ${({ theme }) => primaryBoldFor(theme)} 50%, transparent);
  }

  [data-state='orphan'] & ${EdgeDotIn} {
    background: ${({ theme: { colors = {} } }) =>
      colors.orphanFill ?? '#F4E0A6'};
    border-color: ${({ theme: { colors = {} } }) =>
      colors.orphanBorder ?? '#E6C76A'};
    box-shadow: ${({ theme }) =>
      isLightTheme(theme)
        ? `0 0 0 3px color-mix(in oklch, ${theme.colors?.orphanFill ?? '#FFD980'} 50%, transparent),
           0 0 8px color-mix(in oklch, ${theme.colors?.orphanBorder ?? '#C8973A'} 35%, transparent)`
        : `inset 0 1px 0 0 color-mix(in oklch, white 18%, transparent),
           0 2px 4px rgb(0 0 0 / 0.32),
           0 0 8px color-mix(in oklch, ${theme.colors?.orphanBorder ?? '#E6C76A'} 45%, transparent)`};
  }
  [data-state='orphan'] & ${EdgePillOut} {
    background: color-mix(
      in oklch,
      ${({ theme: { colors = {} } }) => colors.orphanFill ?? '#F4E0A6'} 85%,
      ${({ theme: { colors = {} } }) => colors.surface ?? '#0E0F18'}
    );
    border-color: ${({ theme: { colors = {} } }) =>
      colors.orphanBorder ?? '#E6C76A'};
    color: ${({ theme: { colors = {} } }) => colors.orphanInk ?? '#6E5210'};
  }
`;
